import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import Button from "components/form/flat-button";
import Globalize from "globalize";
import TextField from "../form/text-field";
import { Form } from "formsy-react";
import { DATATEST } from "constants/integration";
import ReCAPTCHA from "react-google-recaptcha";
import config from "site-config";
import { FormatMessage } from "react-globalize";
import Dialog from "components/dialog";
interface IForgotPasswordProps {
    onRequestClose: any;
    email?: string;
    onRequestForgotPassword: ({ email, recaptchaClientKey }) => void;
    onResetState: () => void;
    authentication: any;
    onTrackLoginArticles: () => void;
    onTrackLoginVideo: () => void;
}

interface IForgotPasswordState {
    error?: string;
    value: string;
    recaptcha: string;
    helpModalOpen: boolean;
}

export class ForgotPassword extends React.PureComponent<IForgotPasswordProps, IForgotPasswordState> {
    _form: any;

    muiStyles = {
        bodyStyle: {
            paddingTop: "56.25%",
        },
        contentStyle: {
            maxWidth: "620px",
        },
        iframe: {
            position: "absolute" as "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            width: "100%",
            height: "100%",
        },
    };

    static defaultProps = {
        email: "",
    };

    constructor(props) {
        super(props);
        this.state = {
            value: props.defaultValue,
            error: undefined,
            recaptcha: null,
            helpModalOpen: false,
        };
    }

    handleCloseModal = () => {
        this.props.onRequestClose();
    };

    handleSubmit = ({ input }) => {
        this.props.onRequestForgotPassword({ email: input, recaptchaClientKey: this.state.recaptcha });
    };

    handleTextFieldChange = (e, value) => {
        this.setState({ value });
    };

    handleSubmitButtonTap = () => {
        this._form.submit();
    };

    handleSubmitReCaptcha = (value) => {
        this.setState({ recaptcha: value });
    };

    handleExpireCaptcha = () => {
        this.setState({ recaptcha: null });
    };

    handleHelpModalOpen = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.props.onTrackLoginVideo();
        this.setState({ helpModalOpen: true });
    };

    handleHelpModalClosed = () => {
        this.setState({ helpModalOpen: false });
    };

    render() {
        const { onRequestClose, email, authentication, onTrackLoginArticles } = this.props;
        const { recaptcha, helpModalOpen } = this.state;
        const { error, forgotPasswordSuccess, postingForgotPassword } = authentication;

        if (forgotPasswordSuccess) {
            return (
                <React.Fragment>
                    <h1>{formatMessage("Check your email")}</h1>
                    <p className="forgotPasswordInfo">
                        {formatMessage("We've sent an email with a link to reset your password.")}
                    </p>
                    <p className="forgotPasswordInfo">
                        <FormatMessage
                            path="Can't find the email? Try [spam]looking in your Spam folder[spam] and [hello]adding hello@nuorder.com to your email contacts.[hello]"
                            elements={{
                                spam: (
                                    <a
                                        href="https://helpdesk.nuorder.com/hc/en-us/articles/360057341832-How-to-check-your-Spam-folder"
                                        onClick={onTrackLoginArticles}
                                    />
                                ),
                                hello: (
                                    <a
                                        href="https://helpdesk.nuorder.com/hc/en-us/articles/360057342892-Adding-an-email-to-your-safe-sender-list"
                                        onClick={onTrackLoginArticles}
                                    />
                                ),
                            }}
                        />
                    </p>
                    <Button
                        key="cancel"
                        data-test={DATATEST.MODAL_PROMPT_CANCEL}
                        label={Globalize.formatMessage("Return to login")}
                        primary={true}
                        fullWidth={true}
                        onClick={onRequestClose}
                    />
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                {error && (
                    <div className="alert alert-error" role="alert">
                        {error?.message ? error.message : error}
                    </div>
                )}
                <div data-test={DATATEST.LOGIN.FORGOT_PASSWORD_CONTAINER} className="forgotPasswordContainer">
                    <h1>{formatMessage("Forgot your password?")}</h1>
                    <p className="forgotPasswordInfo">
                        <FormatMessage
                            path="Enter the email you used to create your Lightspeed NuORDER account below. Click [b]send email[b] and you’ll receive an email with further instructions."
                            elements={{ b: <b /> }}
                        />
                    </p>
                    <Form
                        onValidSubmit={this.handleSubmit}
                        className="forgotPasswordForm"
                        ref={(ref) => (this._form = ref)}
                    >
                        <TextField
                            placeholder={formatMessage("Email")}
                            name="input"
                            dataTest={DATATEST.LOGIN.EMAIL}
                            fullWidth={true}
                            requiredErrorText={formatMessage("This field is required.")}
                            onChange={this.handleTextFieldChange}
                            validations="isEmail"
                            validationError={formatMessage("Enter a valid email address.")}
                            allowWhitespace={true}
                            large={true}
                            defaultValue={email || ""}
                            required={true}
                        />
                    </Form>
                    <div className="recaptchaContainer">
                        <ReCAPTCHA
                            sitekey={config.RECAPTCHA_CLIENT_KEY}
                            onChange={this.handleSubmitReCaptcha}
                            onExpired={this.handleExpireCaptcha}
                            data-test={DATATEST.LOGIN.RECAPTCHA}
                        />
                    </div>
                    <div className="actions">
                        <div className="actionButton">
                            <Button
                                key="cancel"
                                data-test={DATATEST.MODAL_PROMPT_CANCEL}
                                label={Globalize.formatMessage("Back")}
                                style={{ marginRight: "24px" } as any}
                                onClick={onRequestClose}
                            />
                        </div>
                        <div>
                            <Button
                                key="submit"
                                data-test={DATATEST.MODAL_PROMPT_CONFIRM}
                                label={Globalize.formatMessage("Send email")}
                                disabled={postingForgotPassword || !recaptcha}
                                primary={true}
                                onClick={this.handleSubmitButtonTap}
                            />
                        </div>
                    </div>
                    <div className="bottomLinks">
                        <FormatMessage
                            path="Video: [video]Need help logging in?[video]"
                            elements={{
                                video: (
                                    <a
                                        href=""
                                        data-test={DATATEST.LOGIN.LOGIN_HELP_LINK}
                                        onClick={this.handleHelpModalOpen}
                                    />
                                ),
                            }}
                        />
                    </div>
                </div>
                <Dialog
                    onRequestClose={this.handleHelpModalClosed}
                    open={helpModalOpen}
                    bodyStyle={this.muiStyles.bodyStyle}
                    contentStyle={this.muiStyles.contentStyle}
                >
                    <iframe
                        src="https://www.screencast.com/users/chris.dunlap/folders/Default/media/32471d58-0024-48d6-878f-d448cf3eb573/embed"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen={true}
                        style={this.muiStyles.iframe}
                        data-test={DATATEST.LOGIN.HELP_VIDEO_IFRAME}
                    />
                </Dialog>
            </React.Fragment>
        );
    }
}

export default ForgotPassword;
